import { useState } from 'react';
import type { MusicTrack } from '../../audio';

export type SoundTab = 'voiceover' | 'music';

export type VoiceoverState = {
  script: string;
  setScript: (next: string) => void;
  audioUrl: string | null;
  setAudioUrl: (next: string | null) => void;
  audioDurationMs: number | null;
  setAudioDurationMs: (next: number | null) => void;
  isGenerating: boolean;
  setIsGenerating: (next: boolean) => void;
  error: string | null;
  setError: (next: string | null) => void;
  /** Volumen 0..1 con el que se va a aplicar la clip al timeline. */
  volume: number;
  setVolume: (next: number) => void;
};

export type MusicState = {
  tracks: MusicTrack[] | null;
  setTracks: (next: MusicTrack[] | null) => void;
  isLoading: boolean;
  setIsLoading: (next: boolean) => void;
  selectedTrackId: string | null;
  setSelectedTrackId: (next: string | null) => void;
  selectedTrackDurationMs: number | null;
  setSelectedTrackDurationMs: (next: number | null) => void;
  /** Volumen 0..1 con el que se va a aplicar la clip al timeline. */
  volume: number;
  setVolume: (next: number) => void;
};

export type SoundPanelState = {
  activeTab: SoundTab;
  setActiveTab: (tab: SoundTab) => void;
  voiceover: VoiceoverState;
  music: MusicState;
};

/**
 * Bundle del estado interactivo del SoundPanel. Se llama desde el componente
 * raíz (`SoundPanel`) para que sobreviva al cerrar/reabrir el popover —
 * `SoundPanelContent` se desmonta cuando el panel cierra, pero el padre no.
 *
 * Los setters provienen de `useState` y son referencialmente estables; las
 * sub-objetos (`voiceover`, `music`) se rebuildean en cada render pero sus
 * properties internas (`script`, `tracks`, etc.) reflejan los valores de
 * `useState` directamente, así que los hooks downstream que dependan de ellas
 * comparan por identidad correctamente cuando desestructuran al inicio.
 */
export const useSoundPanelState = (): SoundPanelState => {
  const [activeTab, setActiveTab] = useState<SoundTab>('voiceover');

  const [voiceoverScript, setVoiceoverScript] = useState('');
  const [voiceoverAudioUrl, setVoiceoverAudioUrl] = useState<string | null>(null);
  const [voiceoverAudioDurationMs, setVoiceoverAudioDurationMs] = useState<number | null>(null);
  const [voiceoverIsGenerating, setVoiceoverIsGenerating] = useState(false);
  const [voiceoverError, setVoiceoverError] = useState<string | null>(null);
  // Voz al 100% por default — el peso de la pista principal.
  const [voiceoverVolume, setVoiceoverVolume] = useState(1);

  const [musicTracks, setMusicTracks] = useState<MusicTrack[] | null>(null);
  const [musicIsLoading, setMusicIsLoading] = useState(false);
  const [musicSelectedTrackId, setMusicSelectedTrackId] = useState<string | null>(null);
  const [musicSelectedTrackDurationMs, setMusicSelectedTrackDurationMs] = useState<number | null>(null);
  // Música al 15% por default — convención: pista de fondo mezclada por debajo de la voz.
  const [musicVolume, setMusicVolume] = useState(0.15);

  return {
    activeTab,
    setActiveTab,
    voiceover: {
      script: voiceoverScript,
      setScript: setVoiceoverScript,
      audioUrl: voiceoverAudioUrl,
      setAudioUrl: setVoiceoverAudioUrl,
      audioDurationMs: voiceoverAudioDurationMs,
      setAudioDurationMs: setVoiceoverAudioDurationMs,
      isGenerating: voiceoverIsGenerating,
      setIsGenerating: setVoiceoverIsGenerating,
      error: voiceoverError,
      setError: setVoiceoverError,
      volume: voiceoverVolume,
      setVolume: setVoiceoverVolume
    },
    music: {
      tracks: musicTracks,
      setTracks: setMusicTracks,
      isLoading: musicIsLoading,
      setIsLoading: setMusicIsLoading,
      selectedTrackId: musicSelectedTrackId,
      setSelectedTrackId: setMusicSelectedTrackId,
      selectedTrackDurationMs: musicSelectedTrackDurationMs,
      setSelectedTrackDurationMs: setMusicSelectedTrackDurationMs,
      volume: musicVolume,
      setVolume: setMusicVolume
    }
  };
};
